import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import api from '../../services/api';

interface Building {
  id: number;
  name: string;
  address: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  flat_no: string;
  building: {
    name: string;
  };
}

interface Notice {
  id: number;
  title: string;
  content: string;
  type: string;
  created_at: string;
}

const AdminDashboard: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [buildingsRes, usersRes, noticesRes] = await Promise.all([
          api.get('/buildings'),
          api.get('/users'),
          api.get('/notices')
        ]);
        
        // Ensure data is an array before setting state
        setBuildings(Array.isArray(buildingsRes.data) ? buildingsRes.data : []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setNotices(Array.isArray(noticesRes.data) ? noticesRes.data : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Set empty arrays on error
        setBuildings([]);
        setUsers([]);
        setNotices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Central Admin Panel</h1>
        <p>Welcome, {user?.name}</p>
      </header>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>System Overview</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">{buildings ? buildings.length : 0}</span>
              <span className="stat-label">Buildings</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{users ? users.length : 0}</span>
              <span className="stat-label">Users</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{notices ? notices.length : 0}</span>
              <span className="stat-label">Notices</span>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3>Building Management</h3>
          <div className="buildings-list">
            {buildings && buildings.length > 0 ? buildings.slice(0, 5).map(building => (
              <div key={building.id} className="building-item">
                <h4>{building.name}</h4>
                <p>{building.address}</p>
              </div>
            )) : (
              <p>No buildings found</p>
            )}
          </div>
          <Link to="/admin/buildings" className="view-all-link">Manage All Buildings</Link>
        </div>

        <div className="dashboard-card">
          <h3>User Management</h3>
          <div className="users-list">
            {users && users.length > 0 ? users.slice(0, 5).map(user => (
              <div key={user.id} className="user-item">
                <h4>{user.name}</h4>
                <p>{user.email}</p>
                <small>{user.role} - {user.building?.name}</small>
              </div>
            )) : (
              <p>No users found</p>
            )}
          </div>
          <Link to="/admin/users" className="view-all-link">Manage All Users</Link>
        </div>

        <div className="dashboard-card">
          <h3>Recent Notices</h3>
          <div className="notices-list">
            {notices && notices.length > 0 ? notices.slice(0, 5).map(notice => (
              <div key={notice.id} className="notice-item">
                <h4>{notice.title}</h4>
                <p>{notice.content}</p>
                <small>{new Date(notice.created_at).toLocaleDateString()}</small>
              </div>
            )) : (
              <p>No notices found</p>
            )}
          </div>
          <Link to="/admin/notices" className="view-all-link">Manage All Notices</Link>
        </div>

        <div className="dashboard-card">
          <h3>System Management</h3>
          <div className="quick-actions">
            <Link to="/admin/cms" className="action-btn">
              Content Management
            </Link>
            <Link to="/admin/committees" className="action-btn">
              Manage Committees
            </Link>
            <Link to="/admin/elections" className="action-btn">
              Manage Elections
            </Link>
            <Link to="/admin/employees" className="action-btn">
              Manage Employees
            </Link>
            <Link to="/admin/reports" className="action-btn">
              View Reports
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
